import type { components, operations } from './schema.gen.ts';

type ErrorResponse = components['schemas']['ErrorResponse'];

type FailsOnTheServer = {
  [O in keyof operations]: operations[O]['responses'] extends { 500: unknown } ? O : never;
}[keyof operations];

type FailsBehindTheScreen = 'allFeedback' | 'gameNotifications' | 'recordHeartbeat';

export type ShowsItsFailure = Exclude<FailsOnTheServer, FailsBehindTheScreen>;

export const serverFailureMessage: Record<ShowsItsFailure, string> = {
  changePhaseLengths: 'Unexpected error when changing a phase length',
  changeRounds: 'Unexpected error when changing the number of rounds',
  chooseSong: 'Unexpected error when choosing a song',
  gameInProgressByCode: 'Unexpected error when looking up the game code',
  guessSong: 'Unexpected error when guessing a song',
  hostGame: 'Unexpected error when creating a game',
  hostPlaylists: 'Unexpected error when loading your playlists',
  joinGame: 'Unexpected error when joining the game',
  leaveFeedback: 'Unexpected error when sending feedback',
  pauseGame: 'Unexpected error when pausing the game',
  playerSession: 'Unexpected error when checking for a game to rejoin',
  searchPlaylists: 'Unexpected error when searching playlists',
  searchSongs: 'Unexpected error when searching a song',
  startGame: 'Unexpected error when starting the game',
  unpauseGame: 'Unexpected error when unpausing the game',
  viewGame: 'Unexpected error when loading the game',
};

export const staleViewSlugs = [
  'phase-mismatch',
  'not-the-chooser',
  'chooser-cannot-guess',
  'already-guessed',
  'countdown-ran-out',
  'countdown-changed',
  'game-over',
  'game-paused',
  'game-already-paused',
  'game-not-paused',
  'phase-cannot-be-paused',
  'rounds-below-current-round',
] as const satisfies ErrorResponse['slug'][];

const staleViewListeners = new Set<() => void>();

export const onStaleView = (resync: () => void): (() => void) => {
  staleViewListeners.add(resync);
  return () => staleViewListeners.delete(resync);
};

export const meetsStaleView = (slug: string): boolean => {
  if (!(staleViewSlugs as readonly string[]).includes(slug)) return false;
  staleViewListeners.forEach((resync) => resync());
  return true;
};

type Answer = { slug: string } | string;

const slugOf = (answer: Answer) => (typeof answer === 'string' ? '' : answer.slug);

export const failedOnTheServer = (answer: Answer) => ['', 'internal-server-error'].includes(slugOf(answer));

export const messageOf = (operation: ShowsItsFailure, answer: ErrorResponse | string): string =>
  typeof answer === 'string' || failedOnTheServer(answer) ? serverFailureMessage[operation] : answer.message;
